import AxiosWeb from "axios";
import CryptoJS from 'crypto-js';
import { store } from "./store";
import { logout } from './slices/auth/authSlice';
import { toast } from 'react-toastify';

async function forceLogoutAndClearCache() {
  try {
    console.log("Performing full cache + storage cleanup...");

    // Clear Local Storage
    localStorage.clear();

    // Clear Session Storage
    sessionStorage.clear();

    // Clear all cookies
    document.cookie.split(";").forEach(cookie => {
      const name = cookie.split("=")[0].trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
    });

    // Clear Cache Storage
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // Unregister service workers to avoid stale JS bundles
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (let reg of registrations) {
        await reg.unregister();
      }
    }

    // Dispatch Redux logout
    store.dispatch(logout());

    // Show message
    toast.error("Your session has expired. Please login again to continue.");

    // Force a HARD reload without cache
    window.location.href = "/";

  } catch (err) {
    console.error("Full logout cleanup failed:", err);
  }
}


let isToastDisplayed = false;

// Working encryption function (SHA256 hashed key)
const encryptPayload = (payload) => {
  const key = process.env.REACT_APP_AES_KEY;

  // Convert payload to JSON string
  const rawData = typeof payload === 'string' ? payload : JSON.stringify(payload);

  // Hash the key with SHA256 (this was the missing piece!)
  const hashedKey = CryptoJS.SHA256(key);
  const iv = CryptoJS.lib.WordArray.random(16);

  const encrypted = CryptoJS.AES.encrypt(rawData, hashedKey, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  // Combine IV + ciphertext as raw bytes
  const combined = iv.clone().concat(encrypted.ciphertext);

  // Encode as Base64 string for transport
  return CryptoJS.enc.Base64.stringify(combined);
};

// Decryption function
const decryptPayload = (encryptedData) => {
  const key = process.env.REACT_APP_AES_KEY;

  // Hash the key with SHA256
  const hashedKey = CryptoJS.SHA256(key);

  // Decode base64
  const combined = CryptoJS.enc.Base64.parse(encryptedData);

  // Extract IV (first 16 bytes) and ciphertext (rest)
  const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4));
  const ciphertext = CryptoJS.lib.WordArray.create(combined.words.slice(4));

  // Decrypt
  const decrypted = CryptoJS.AES.decrypt(
    { ciphertext: ciphertext },
    hashedKey,
    {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    }
  );

  const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);

  try {
    return JSON.parse(decryptedString);
  } catch {
    return decryptedString;
  }
};

//* Request Interceptor
AxiosWeb.interceptors.request.use(function (config) {
  const WSO2Token = process.env.REACT_APP_WSO2_TOKEN;
  const token = localStorage.getItem('accessToken');

  if (token) {
    config["headers"]["Custom"] = `Bearer ${token}`;
  }
  config["headers"]["Authorization"] = WSO2Token;

  // Check if encryption is enabled for this request
  if (config.encrypt === true && config.data) {
    try {
      const encryptedData = encryptPayload(config.data);
      config.data = { data: encryptedData };
      config.headers['X-Encryption'] = 'AES-256-CBC-SHA256';
    } catch (error) {
      console.error('Encryption failed:', error);
      // Continue with unencrypted data if encryption fails
    }
  }

  return config;
}, function (error) {
  return Promise.reject(error);
});

//* Response Interceptor
AxiosWeb.interceptors.response.use((response) => {
  if (response?.data?.response_code === 401) {
    forceLogoutAndClearCache();
    return;
  }

  // Check if response needs decryption
  if (response.config.encrypt === true && response.data?.data && typeof response.data.data === 'string') {
    try {
      const decryptedData = decryptPayload(response.data.data);
      response.data = decryptedData;
    } catch (error) {
      console.error('Decryption failed:', error);
      // Continue with encrypted data if decryption fails
    }
  }

  return response;
}, (error) => {
  if (typeof error?.response === "undefined") {
    if (!isToastDisplayed) {
      isToastDisplayed = true;
      toast.error("Network Error");
      setTimeout(() => {
        isToastDisplayed = false;
      }, 3000);
    }
    return Promise.reject(error);
  }
  else if (error?.status === 401) {
    forceLogoutAndClearCache();
    return;
  }

  const errCodes = [500, 502, 503, 504, 404, 401, undefined];
  if (errCodes.includes(error?.status)) {
    if (!isToastDisplayed) {
      isToastDisplayed = true;
      const errorMessage = {
        500: "We are facing technical issue. Please contact support team",
        502: "We are facing technical issue. Please contact support team",
        503: "We are facing technical issue. Please contact support team",
        504: "We are facing technical issue. Please contact support team",
        404: "We are facing technical issue. Please contact support team",
        401: "Your session has expired. Please login again to continue.",
        undefined: "Network Error, Please check your internet connection."
      }[error?.status] || "We are facing technical issue. Please contact support team";

      toast.error(errorMessage);
      setTimeout(() => {
        isToastDisplayed = false;
      }, 3000);
    }
    return Promise.reject(error);
  }

  return Promise.reject(error);
});

export default AxiosWeb;
